/*
 * Copyright (c) 2019. Nedaware SL
 */

import {PlacedWidget} from "./widget";
import {AVAILABLE_WIDGETS} from "./available-widgets";
import {SessionService} from "../../session/session.service";
import {Subject} from "rxjs";

/**
 * Nedaware S.L.
 * @author David Campos Rodríguez <david.campos.r96@gmail.com>
 */
export class WidgetDb {
    public readonly DATABASE_NAME = 'cnc-inicio-widgets';
    public readonly DATABASE_VERSION = 1;

    private _db: IDBDatabase = null;
    private _waitingForDb = new Subject();

    /**
     * Completes when the db is obtained
     */
    public waitingForDb = this._waitingForDb.asObservable();

    private get _userId(): string | null {
        if (this._session.isLogged()) {
            return this._session.token.username;
        } else {
            return null;
        }
    }

    constructor(private _session: SessionService) {
        if (window.indexedDB) {
            const dbRequest = indexedDB.open(this.DATABASE_NAME, this.DATABASE_VERSION);
            dbRequest.onerror = (event) => console.error(event);
            dbRequest.onsuccess = (event) => {
                this._db = dbRequest.result;
                this._waitingForDb.complete();
            };
            dbRequest.onupgradeneeded = WidgetDb._dbUpgrade.bind(this);
        }
    }

    private static _dbUpgrade(event: IDBVersionChangeEvent) {
        console.log('DB: upgrade ' + event.oldVersion + ' -> ' + event.newVersion);
        const db = (event.target as any).result as IDBDatabase;
        const widgetStore = db.createObjectStore('widgets', {keyPath: 'id', autoIncrement: true});
        widgetStore.createIndex('user', 'user', {unique: false});
    }

    public retrieve(): Promise<PlacedWidget[]> {
        return new Promise<PlacedWidget[]>((resolve, reject) => {
            if (this._db && this._userId) {
                const widgets: PlacedWidget[] = [];
                this._initTransaction(false)
                    .index('user')
                    .openCursor(IDBKeyRange.only(this._userId)).onsuccess =
                    (event) => {
                        const cursor = (event.target as any).result as IDBCursorWithValue;
                        if (cursor) {
                            widgets.push(new PlacedWidget(
                                this._findWidget(cursor.value.key),
                                cursor.value.row,
                                cursor.value.col,
                                cursor.value.rows,
                                cursor.value.cols,
                                cursor.value.id
                            ));
                            cursor.continue();
                        } else {
                            resolve(widgets);
                        }
                    };
            } else {
                reject();
            }
        });
    }

    public update(widget: PlacedWidget) {
        if (!widget.id) {
            this.add(widget);
        } else if (this._db) {
            const objectStore = this._initTransaction();
            objectStore.get(widget.id).onsuccess =
                (event) => {
                    const data = (event.target as any).result;
                    data.row = widget.row;
                    data.col = widget.column;
                    data.rows = widget.rows;
                    data.cols = widget.cols;
                    objectStore.put(data);
                };
        }
    }

    public add(widget: PlacedWidget) {
        if (this._db && this._userId) {
            const request = this._initTransaction()
                .add({
                    user: this._userId,
                    key: widget.component.widgetKey,
                    row: widget.row,
                    col: widget.column,
                    rows: widget.rows,
                    cols: widget.cols
                });
            request.onsuccess = (event) => widget.id = (event.target as any).result;
        }
    }

    public remove(widget: PlacedWidget) {
        if (this._db && widget.id) {
            this._initTransaction()
                .delete(widget.id);
        }
    }

    private _initTransaction(withWrite = true): IDBObjectStore {
        return this._db.transaction(['widgets'], withWrite ? 'readwrite' : 'readonly')
            .objectStore('widgets');
    }

    private _findWidget(widgetKey: string) {
        return AVAILABLE_WIDGETS.find(w => w.widgetKey === widgetKey);
    }
}
